import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import Card from "../layout/Card";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

const selectionModes = [
  { id: "individual", name: "Individual" },
  { id: "users", name: "Multi-Select Users" },
  { id: "projectCalls", name: "By Project Calls" },
];

const builderModes = [
  { id: "callScope", name: "Call Scope Builder" },
  { id: "manual", name: "Manual JSON" },
];

const statusOptions = [
  { id: "all", name: "All" },
  { id: "queued", name: "Queued" },
  { id: "requested", name: "Requested" },
  { id: "in_progress", name: "In Progress" },
  { id: "completed", name: "Completed" },
  { id: "processed", name: "Processed" },
  { id: "failed", name: "Failed" },
  { id: "expired", name: "Expired" },
];

const statusColor = (status) => {
  if (status === "completed") return "success";
  if (status === "failed" || status === "expired") return "error";
  if (status === "requested" || status === "in_progress") return "warning";
  return "default";
};

const DEFAULT_TABLES_JSON = JSON.stringify(
  [{ name: "surveyanswers", filter: {}, columns: [] }],
  null,
  2,
);

const ForensicRequestManager = () => {
  const { executeAction, getListFromServer } = useData();
  const { showMessage } = usePage();

  const [builderMode, setBuilderMode] = useState("callScope");

  const [selectionMode, setSelectionMode] = useState("individual");
  const [selectableUsers, setSelectableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [projectUsers, setProjectUsers] = useState([]);

  const [deviceId, setDeviceId] = useState("");
  const [requestName, setRequestName] = useState("");
  const [priority, setPriority] = useState(1);
  const [maxRecordsPerTable, setMaxRecordsPerTable] = useState(5000);
  const [expiresAt, setExpiresAt] = useState("");
  const [notes, setNotes] = useState("");
  const [tablesJson, setTablesJson] = useState(DEFAULT_TABLES_JSON);
  const [defaultFilterJson, setDefaultFilterJson] = useState("{}");
  const [submitting, setSubmitting] = useState(false);

  const [scopeCallIds, setScopeCallIds] = useState("");
  const [scopeProjectId, setScopeProjectId] = useState("");
  const [scopeLocationId, setScopeLocationId] = useState("");
  const [scopeDateFrom, setScopeDateFrom] = useState("");
  const [scopeDateTo, setScopeDateTo] = useState("");
  const [includeCalls, setIncludeCalls] = useState(true);
  const [includeCallResults, setIncludeCallResults] = useState(true);
  const [includeSurveyAnswers, setIncludeSurveyAnswers] = useState(true);
  const [imagesOnly, setImagesOnly] = useState(false);

  const [statusFilter, setStatusFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [loadingList, setLoadingList] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuRow, setActionMenuRow] = useState(null);

  const [artifactBatchFilter, setArtifactBatchFilter] = useState("");
  const [artifactRequestIdFilter, setArtifactRequestIdFilter] = useState("");
  const [artifactRows, setArtifactRows] = useState([]);
  const [artifactTotal, setArtifactTotal] = useState(0);
  const [artifactPageNumber, setArtifactPageNumber] = useState(0);
  const [artifactRecordsPerPage] = useState(20);
  const [loadingArtifacts, setLoadingArtifacts] = useState(false);
  const [artifactBrowserDialogOpen, setArtifactBrowserDialogOpen] =
    useState(false);

  const [artifactDialogOpen, setArtifactDialogOpen] = useState(false);
  const [selectedArtifact, setSelectedArtifact] = useState(null);
  const [artifactPreviewOffset, setArtifactPreviewOffset] = useState(0);
  const [artifactPreviewLimit] = useState(100);
  const [artifactPreview, setArtifactPreview] = useState(null);
  const [loadingArtifactPreview, setLoadingArtifactPreview] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareResult, setCompareResult] = useState(null);
  const [recoveringImages, setRecoveringImages] = useState(false);
  const [resolvingConflicts, setResolvingConflicts] = useState(false);

  const [lastBatchId, setLastBatchId] = useState("");
  const [formError, setFormError] = useState("");

  const pageCount = useMemo(() => {
    if (!recordsPerPage || recordsPerPage <= 0) return 1;
    const pages = Math.ceil(total / recordsPerPage);
    return pages > 0 ? pages : 1;
  }, [recordsPerPage, total]);

  const artifactPageCount = useMemo(() => {
    if (!artifactRecordsPerPage || artifactRecordsPerPage <= 0) return 1;
    const pages = Math.ceil(artifactTotal / artifactRecordsPerPage);
    return pages > 0 ? pages : 1;
  }, [artifactRecordsPerPage, artifactTotal]);

  const getId = (item) => item?.id || item?._id || "";

  const parseJsonSafe = (textValue, fallback) => {
    const value = `${textValue || ""}`.trim();
    if (!value) return fallback;
    return JSON.parse(value);
  };

  const loadSelectableUsers = async (search = "") => {
    const result = await executeAction({
      entityName: "ForensicExtractRequest",
      action: "getSelectableUsers",
      data: { search, limit: 100 },
    });

    if (result?.isSuccessful && Array.isArray(result.data)) {
      setSelectableUsers(result.data);
    }
  };

  const loadProjects = async () => {
    const list = await getListFromServer("Project", { isActive: true });
    setProjects(Array.isArray(list) ? list : []);
  };

  const loadProjectUsers = async (nextProjectId) => {
    if (!nextProjectId) {
      setProjectUsers([]);
      return;
    }

    const result = await executeAction({
      entityName: "ForensicExtractRequest",
      action: "getProjectCallUsers",
      data: { projectId: nextProjectId },
    });

    if (result?.isSuccessful && Array.isArray(result.data)) {
      setProjectUsers(result.data);
      return;
    }

    setProjectUsers([]);
  };

  const loadRequests = async (
    nextPage = pageNumber,
    nextStatus = statusFilter,
  ) => {
    setLoadingList(true);

    try {
      const filter = {};
      if (nextStatus && nextStatus !== "all") {
        filter.status = nextStatus;
      }

      const result = await executeAction({
        entityName: "ForensicExtractRequest",
        action: "listRequests",
        data: {
          pageNumber: nextPage,
          recordsPerPage,
          filter,
        },
      });

      if (result?.isSuccessful && result.data) {
        setRows(Array.isArray(result.data.records) ? result.data.records : []);
        setTotal(Number(result.data.total || 0));
      } else {
        setRows([]);
        setTotal(0);
      }
    } catch (err) {
      setRows([]);
      setTotal(0);
      showMessage(`Failed to load requests: ${err.message}`, "error");
    } finally {
      setLoadingList(false);
    }
  };

  const loadArtifacts = async (
    nextPage = artifactPageNumber,
    nextBatchFilter = artifactBatchFilter,
    nextRequestIdFilter = artifactRequestIdFilter,
  ) => {
    setLoadingArtifacts(true);

    try {
      const filter = {};
      if (nextBatchFilter && nextBatchFilter.trim()) {
        filter.requestBatchId = nextBatchFilter.trim();
      }
      if (nextRequestIdFilter && nextRequestIdFilter.trim()) {
        filter.requestId = nextRequestIdFilter.trim();
      }

      const result = await executeAction({
        entityName: "ForensicExtractRequest",
        action: "listArtifacts",
        data: {
          pageNumber: nextPage,
          recordsPerPage: artifactRecordsPerPage,
          filter,
        },
      });

      if (result?.isSuccessful && result.data) {
        setArtifactRows(
          Array.isArray(result.data.records) ? result.data.records : [],
        );
        setArtifactTotal(Number(result.data.total || 0));
      } else {
        setArtifactRows([]);
        setArtifactTotal(0);
      }
    } catch (err) {
      setArtifactRows([]);
      setArtifactTotal(0);
      showMessage(`Failed to load artifacts: ${err.message}`, "error");
    } finally {
      setLoadingArtifacts(false);
    }
  };

  const loadArtifactPreview = async (artifactId, nextOffset = 0) => {
    if (!artifactId) return;

    setLoadingArtifactPreview(true);
    try {
      const result = await executeAction({
        entityName: "ForensicExtractRequest",
        action: "getArtifactRecords",
        data: {
          artifactId,
          offset: nextOffset,
          limit: artifactPreviewLimit,
        },
      });

      if (!result?.isSuccessful || !result.data?.artifact) {
        setFormError(result?.message || "Failed to load artifact records");
        return;
      }

      setArtifactPreview(result.data.artifact);
      setArtifactPreviewOffset(nextOffset);
    } catch (err) {
      setFormError(err.message || "Failed to load artifact records");
    } finally {
      setLoadingArtifactPreview(false);
    }
  };

  useEffect(() => {
    loadSelectableUsers();
    loadProjects();
  }, []);

  useEffect(() => {
    loadRequests(0, statusFilter);
  }, [statusFilter, recordsPerPage]);

  useEffect(() => {
    if (selectionMode !== "projectCalls") return;
    loadProjectUsers(projectId);
  }, [selectionMode, projectId]);

  const buildPayload = () => {
    const parsedTables = parseJsonSafe(tablesJson, []);
    if (!Array.isArray(parsedTables)) {
      throw new Error("tables must be a JSON array");
    }

    const parsedDefaultFilter = parseJsonSafe(defaultFilterJson, {});
    if (parsedDefaultFilter && typeof parsedDefaultFilter !== "object") {
      throw new Error("defaultFilter must be a JSON object");
    }

    const payload = {
      selectionMode,
      name: `${requestName || ""}`.trim(),
      tables: parsedTables,
      defaultFilter: parsedDefaultFilter,
      deviceId: deviceId ? String(deviceId).trim() : null,
      priority: Number(priority),
      maxRecordsPerTable: Number(maxRecordsPerTable),
      expiresAt: expiresAt || null,
      notes,
    };

    if (selectionMode === "individual") {
      if (!selectedUser?.id) {
        throw new Error("Please select one user");
      }
      payload.userId = selectedUser.id;
    }

    if (selectionMode === "users") {
      const ids = selectedUsers.map((user) => user?.id).filter(Boolean);
      if (ids.length === 0) {
        throw new Error("Please select at least one user");
      }
      payload.userIds = ids;
    }

    if (selectionMode === "projectCalls") {
      if (!projectId) {
        throw new Error("Please select a project");
      }
      payload.projectId = projectId;
    }

    return payload;
  };

  const handleCreateRequests = async () => {
    setFormError("");

    try {
      const payload = buildPayload();
      setSubmitting(true);

      const result = await executeAction({
        entityName: "ForensicExtractRequest",
        action: "createRequests",
        data: payload,
      });

      if (!result?.isSuccessful) {
        setFormError(result?.message || "Failed to create requests");
        return;
      }

      setLastBatchId(result?.data?.requestBatchId || "");
      showMessage(`Created ${result?.data?.createdCount || 0} request(s)`);
      await loadRequests(0, statusFilter);
    } catch (err) {
      setFormError(err.message || "Failed to create requests");
    } finally {
      setSubmitting(false);
    }
  };

  const buildCallScopePayload = () => {
    const callIds = `${scopeCallIds || ""}`
      .split(/[\n,\s]+/)
      .map((value) => value.trim())
      .filter(Boolean);

    if (
      callIds.length === 0 &&
      !scopeProjectId &&
      !scopeLocationId &&
      !scopeDateFrom &&
      !scopeDateTo
    ) {
      throw new Error(
        "Provide call IDs or at least one scope filter (project, location, date range)",
      );
    }

    if (!includeCalls && !includeCallResults && !includeSurveyAnswers) {
      throw new Error(
        "Select at least one artifact: calls, callresults, or surveyanswers",
      );
    }

    return {
      name: `${requestName || ""}`.trim(),
      deviceId: deviceId ? String(deviceId).trim() : null,
      priority: Number(priority),
      maxRecordsPerTable: Number(maxRecordsPerTable),
      expiresAt: expiresAt || null,
      notes,
      scope: {
        callIds,
        projectId: scopeProjectId || null,
        locationId: scopeLocationId || null,
        dateFrom: scopeDateFrom || null,
        dateTo: scopeDateTo || null,
      },
      include: {
        calls: includeCalls,
        callresults: includeCallResults,
        surveyanswers: includeSurveyAnswers || imagesOnly,
        imagesOnly,
      },
    };
  };

  const handleCreateCallScopeRequests = async () => {
    setFormError("");

    try {
      const payload = buildCallScopePayload();
      setSubmitting(true);

      const result = await executeAction({
        entityName: "ForensicExtractRequest",
        action: "createCallScopedRequests",
        data: payload,
      });

      if (!result?.isSuccessful) {
        setFormError(
          result?.message || "Failed to create call-scoped requests",
        );
        return;
      }

      setLastBatchId(result?.data?.requestBatchId || "");
      showMessage(`Created ${result?.data?.createdCount || 0} request(s)`);
      await loadRequests(0, statusFilter);
    } catch (err) {
      setFormError(err.message || "Failed to create call-scoped requests");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBatchAction = async (requestBatchId, action) => {
    if (!requestBatchId || !action) return;

    setFormError("");

    try {
      const result = await executeAction({
        entityName: "ForensicExtractRequest",
        action,
        data: { requestBatchId },
      });

      if (!result?.isSuccessful) {
        setFormError(result?.message || `Failed to ${action}`);
        return;
      }

      const modifiedCount = Number(result?.data?.modifiedCount || 0);
      showMessage(`${action} updated ${modifiedCount} request(s)`);
      await loadRequests(pageNumber, statusFilter);
    } catch (err) {
      setFormError(err.message || `Failed to ${action}`);
    }
  };

  const openActionMenu = (event, row) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuRow(row);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuRow(null);
  };

  const runMenuAction = async (action) => {
    if (!actionMenuRow?.requestBatchId) {
      closeActionMenu();
      return;
    }

    const batchId = actionMenuRow.requestBatchId;
    closeActionMenu();
    await handleBatchAction(batchId, action);
  };

  const handleViewExtractsFromMenu = async () => {
    const requestId = actionMenuRow?.id || actionMenuRow?._id || "";
    closeActionMenu();

    setArtifactRequestIdFilter(String(requestId || ""));
    setArtifactBatchFilter("");
    setArtifactPageNumber(0);
    setArtifactBrowserDialogOpen(true);
    await loadArtifacts(0, "", String(requestId || ""));
  };

  const handleOpenArtifactsBrowser = async () => {
    setArtifactBrowserDialogOpen(true);
    setArtifactRequestIdFilter("");
    setArtifactPageNumber(0);
    await loadArtifacts(0, artifactBatchFilter, "");
  };

  const handleOpenArtifactPreview = async (row) => {
    const artifactId = getId(row);
    if (!artifactId) return;

    setSelectedArtifact(row);
    setArtifactPreview(null);
    setArtifactDialogOpen(true);
    await loadArtifactPreview(artifactId, 0);
  };

  const runCompareByArtifactId = async (artifactId) => {
    if (!artifactId) return;

    setFormError("");
    setCompareLoading(true);

    try {
      const result = await executeAction({
        entityName: "ForensicExtractRequest",
        action: "compareArtifactWithServer",
        data: { artifactId },
      });

      if (!result?.isSuccessful || !result.data) {
        setFormError(result?.message || "Compare failed");
        return;
      }

      setCompareResult(result.data);
    } catch (err) {
      setFormError(err.message || "Compare failed");
    } finally {
      setCompareLoading(false);
    }
  };

  const handleCompareArtifact = async (row) => {
    const artifactId = getId(row);
    if (!artifactId) return;

    setCompareResult(null);
    setCompareDialogOpen(true);
    await runCompareByArtifactId(artifactId);
  };

  const handleRecoverMissingImages = async () => {
    const artifactId = compareResult?.artifactId;
    if (!artifactId) return;

    setFormError("");
    setRecoveringImages(true);
    try {
      const result = await executeAction({
        entityName: "ForensicExtractRequest",
        action: "recoverMissingSurveyImagesFromArtifact",
        data: { artifactId },
      });

      if (!result?.isSuccessful || !result.data?.totals) {
        setFormError(result?.message || "Recovery failed");
        return;
      }

      const totals = result.data.totals;
      showMessage(
        `Recovered ${Number(totals.restored || 0)} image(s); missing=${Number(
          totals.missingInSurveyImages || 0,
        )}, copyErrors=${Number(totals.copyErrors || 0)}`,
      );

      await runCompareByArtifactId(artifactId);
    } catch (err) {
      setFormError(err.message || "Recovery failed");
    } finally {
      setRecoveringImages(false);
    }
  };

  const handleResolveConflicts = async () => {
    const artifactId = compareResult?.artifactId;
    if (!artifactId) return;

    setFormError("");
    setResolvingConflicts(true);
    try {
      const result = await executeAction({
        entityName: "ForensicExtractRequest",
        action: "resolveArtifactConflicts",
        data: {
          artifactId,
          applyMissing: true,
          applyChanged: true,
        },
      });

      if (!result?.isSuccessful || !result.data) {
        setFormError(result?.message || "Resolve conflicts failed");
        return;
      }

      const writeSummary = result.data?.writeSummary || {};
      showMessage(
        `Resolved conflicts: modified=${Number(writeSummary.modifiedCount || 0)}, upserted=${Number(writeSummary.upsertedCount || 0)}`,
      );

      await runCompareByArtifactId(artifactId);
    } catch (err) {
      setFormError(err.message || "Resolve conflicts failed");
    } finally {
      setResolvingConflicts(false);
    }
  };

  return (
    <Card
      title="Forensic Extract Requests"
      sx={{ minWidth: 900, flex: 1 }}
    >
      <Stack spacing={2}>
        {formError ? <Alert severity="error">{formError}</Alert> : null}
        {lastBatchId ? (
          <Alert severity="success">Last batch: {lastBatchId}</Alert>
        ) : null}

        <Grid
          container
          spacing={2}
          sx={{ padding: "20px" }}
        >
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Builder Mode</InputLabel>
              <Select
                value={builderMode}
                label="Builder Mode"
                onChange={(event) => setBuilderMode(event.target.value)}
              >
                {builderModes.map((mode) => (
                  <MenuItem
                    key={mode.id}
                    value={mode.id}
                  >
                    {mode.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {builderMode === "callScope" ? (
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                Call Scope Builder creates mobile-compatible filters using call
                local IDs and callresult local IDs. Target users are inferred
                from matching calls.
              </Alert>
            </Grid>
          ) : null}

          {builderMode === "manual" ? (
            <>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Selection Mode</InputLabel>
                  <Select
                    value={selectionMode}
                    label="Selection Mode"
                    onChange={(event) => setSelectionMode(event.target.value)}
                  >
                    {selectionModes.map((mode) => (
                      <MenuItem
                        key={mode.id}
                        value={mode.id}
                      >
                        {mode.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 9 }}>
                {selectionMode === "individual" ? (
                  <Autocomplete
                    options={selectableUsers}
                    value={selectedUser}
                    getOptionLabel={(option) => option?.name || ""}
                    onChange={(_, newValue) => setSelectedUser(newValue)}
                    onInputChange={(_, value) => loadSelectableUsers(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="User"
                        placeholder="Search user"
                      />
                    )}
                  />
                ) : null}

                {selectionMode === "users" ? (
                  <Autocomplete
                    multiple
                    options={selectableUsers}
                    value={selectedUsers}
                    getOptionLabel={(option) => option?.name || ""}
                    onChange={(_, newValue) => setSelectedUsers(newValue)}
                    onInputChange={(_, value) => loadSelectableUsers(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Users"
                        placeholder="Search users"
                      />
                    )}
                  />
                ) : null}

                {selectionMode === "projectCalls" ? (
                  <Stack spacing={1}>
                    <FormControl fullWidth>
                      <InputLabel>Project</InputLabel>
                      <Select
                        value={projectId}
                        label="Project"
                        onChange={(event) => setProjectId(event.target.value)}
                      >
                        {projects.map((project) => (
                          <MenuItem
                            key={project.id}
                            value={project.id}
                          >
                            {project.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="body2">
                      Project users with calls: {projectUsers.length}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                    >
                      {projectUsers.slice(0, 10).map((item) => (
                        <Chip
                          key={item.id}
                          size="small"
                          label={`${item.name} (${item.callCount})`}
                        />
                      ))}
                    </Stack>
                  </Stack>
                ) : null}
              </Grid>
            </>
          ) : null}

          {builderMode === "callScope" ? (
            <>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Project (Optional)</InputLabel>
                  <Select
                    value={scopeProjectId}
                    label="Project (Optional)"
                    onChange={(event) => setScopeProjectId(event.target.value)}
                  >
                    <MenuItem value="">All Projects</MenuItem>
                    {projects.map((project) => (
                      <MenuItem
                        key={project.id}
                        value={project.id}
                      >
                        {project.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Location ID (Optional)"
                  value={scopeLocationId}
                  onChange={(event) => setScopeLocationId(event.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  fullWidth
                  label="Date From"
                  type="date"
                  value={scopeDateFrom}
                  onChange={(event) => setScopeDateFrom(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  fullWidth
                  label="Date To"
                  type="date"
                  value={scopeDateTo}
                  onChange={(event) => setScopeDateTo(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Call IDs (Optional)"
                  placeholder="Paste one or more call IDs separated by commas, spaces, or new lines"
                  value={scopeCallIds}
                  onChange={(event) => setScopeCallIds(event.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Stack
                  direction="row"
                  spacing={2}
                  flexWrap="wrap"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeCalls}
                        onChange={(event) =>
                          setIncludeCalls(event.target.checked)
                        }
                      />
                    }
                    label="Include Calls"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeCallResults}
                        onChange={(event) =>
                          setIncludeCallResults(event.target.checked)
                        }
                      />
                    }
                    label="Include CallResults"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeSurveyAnswers}
                        onChange={(event) =>
                          setIncludeSurveyAnswers(event.target.checked)
                        }
                      />
                    }
                    label="Include SurveyAnswers"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={imagesOnly}
                        onChange={(event) =>
                          setImagesOnly(event.target.checked)
                        }
                      />
                    }
                    label="Images Only (SurveyAnswers)"
                  />
                </Stack>
              </Grid>
            </>
          ) : null}

          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              label="Request Name"
              value={requestName}
              onChange={(event) => setRequestName(event.target.value)}
              placeholder="Fix Orphaned"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              label="Device ID (Optional)"
              value={deviceId}
              onChange={(event) => setDeviceId(event.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              label="Priority"
              type="number"
              value={priority}
              onChange={(event) => setPriority(event.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              label="Max Records / Table"
              type="number"
              value={maxRecordsPerTable}
              onChange={(event) => setMaxRecordsPerTable(event.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Expires At"
              type="datetime-local"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </Grid>
          {builderMode === "manual" ? (
            <>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  label="Tables JSON"
                  value={tablesJson}
                  onChange={(event) => setTablesJson(event.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  label="Default Filter JSON"
                  value={defaultFilterJson}
                  onChange={(event) => setDefaultFilterJson(event.target.value)}
                />
              </Grid>
            </>
          ) : null}
        </Grid>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
        >
          <Button
            variant="contained"
            onClick={
              builderMode === "callScope"
                ? handleCreateCallScopeRequests
                : handleCreateRequests
            }
            disabled={submitting}
          >
            {submitting ? "Creating..." : "Create Requests"}
          </Button>
        </Stack>

        <Box
          sx={{
            borderTop: "1px solid #ddd",
            pt: 2,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="h6">Request Status</Typography>
            <Stack
              direction="row"
              spacing={1}
            >
              <Button
                variant="outlined"
                onClick={handleOpenArtifactsBrowser}
              >
                View Extracts
              </Button>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(event) => {
                    setPageNumber(0);
                    setStatusFilter(event.target.value);
                  }}
                >
                  {statusOptions.map((option) => (
                    <MenuItem
                      key={option.id}
                      value={option.id}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 130 }}>
                <InputLabel>Rows</InputLabel>
                <Select
                  value={recordsPerPage}
                  label="Rows"
                  onChange={(event) => {
                    setPageNumber(0);
                    setRecordsPerPage(Number(event.target.value));
                  }}
                >
                  {[10, 20, 50, 100].map((size) => (
                    <MenuItem
                      key={size}
                      value={size}
                    >
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                onClick={() => loadRequests(pageNumber, statusFilter)}
                disabled={loadingList}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Created</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tables</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>Last Error</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {row.createdAt
                        ? new Date(row.createdAt).toLocaleString()
                        : "-"}
                    </TableCell>
                    <TableCell>{row.name || "-"}</TableCell>
                    <TableCell>{row.userName || row.userId || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.status || "-"}
                        color={statusColor(row.status)}
                      />
                    </TableCell>
                    <TableCell>
                      {Array.isArray(row.tables) ? row.tables.length : 0}
                    </TableCell>
                    <TableCell>{row.priority ?? "-"}</TableCell>
                    <TableCell>{row.requestBatchId || "-"}</TableCell>
                    <TableCell>{row.lastError || "-"}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        disabled={!row.requestBatchId}
                        onClick={(event) => openActionMenu(event, row)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!loadingList && rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                    >
                      No requests found
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack
            direction="row"
            justifyContent="flex-end"
            mt={1}
          >
            <Pagination
              page={pageNumber + 1}
              count={pageCount}
              onChange={(_, value) => {
                const nextPage = value - 1;
                setPageNumber(nextPage);
                loadRequests(nextPage, statusFilter);
              }}
            />
          </Stack>

          <Menu
            anchorEl={actionMenuAnchor}
            open={Boolean(actionMenuAnchor)}
            onClose={closeActionMenu}
          >
            <MenuItem onClick={handleViewExtractsFromMenu}>
              View Extracts
            </MenuItem>
            <MenuItem onClick={() => runMenuAction("retryBatch")}>
              Retry
            </MenuItem>
            <MenuItem onClick={() => runMenuAction("expireBatch")}>
              Expire
            </MenuItem>
            <MenuItem onClick={() => runMenuAction("cancelBatch")}>
              Cancel
            </MenuItem>
          </Menu>
        </Box>

        <Dialog
          open={artifactBrowserDialogOpen}
          onClose={() => setArtifactBrowserDialogOpen(false)}
          fullWidth
          maxWidth="xl"
        >
          <DialogTitle>Extract Artifacts</DialogTitle>
          <DialogContent>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="space-between"
              alignItems="center"
              mb={1}
              mt={0.5}
            >
              <Typography variant="body2">
                Review uploaded forensic extract artifacts and open record
                previews.
              </Typography>
              <Stack
                direction="row"
                spacing={1}
              >
                <TextField
                  size="small"
                  label="Extract Batch ID"
                  value={artifactBatchFilter}
                  onChange={(event) =>
                    setArtifactBatchFilter(event.target.value)
                  }
                  placeholder="Optional"
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    setArtifactRequestIdFilter("");
                    setArtifactPageNumber(0);
                    loadArtifacts(0, artifactBatchFilter, "");
                  }}
                  disabled={loadingArtifacts}
                >
                  Load
                </Button>
              </Stack>
            </Stack>

            {artifactRequestIdFilter ? (
              <Alert
                severity="info"
                sx={{ mb: 1 }}
              >
                Showing artifacts for request {artifactRequestIdFilter}
              </Alert>
            ) : null}

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Captured</TableCell>
                    <TableCell>Batch</TableCell>
                    <TableCell>Table</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Records</TableCell>
                    <TableCell>Images</TableCell>
                    <TableCell>Request ID</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {artifactRows.map((row) => (
                    <TableRow key={getId(row)}>
                      <TableCell>
                        {row.capturedAt
                          ? new Date(row.capturedAt).toLocaleString()
                          : "-"}
                      </TableCell>
                      <TableCell>{row.extractBatchId || "-"}</TableCell>
                      <TableCell>{row.tableName || "-"}</TableCell>
                      <TableCell>{row.userName || row.userId || "-"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status || "-"}
                          color={statusColor(row.status)}
                        />
                      </TableCell>
                      <TableCell>{row.recordCount ?? 0}</TableCell>
                      <TableCell>{row.imageUploadCount ?? 0}</TableCell>
                      <TableCell>{row.requestId || "-"}</TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={0.5}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleOpenArtifactPreview(row)}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleCompareArtifact(row)}
                          >
                            Compare
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loadingArtifacts && artifactRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        align="center"
                      >
                        No artifacts found
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack
              direction="row"
              justifyContent="flex-end"
              mt={1}
            >
              <Pagination
                page={artifactPageNumber + 1}
                count={artifactPageCount}
                onChange={(_, value) => {
                  const nextPage = value - 1;
                  setArtifactPageNumber(nextPage);
                  loadArtifacts(
                    nextPage,
                    artifactBatchFilter,
                    artifactRequestIdFilter,
                  );
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setArtifactBrowserDialogOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={artifactDialogOpen}
          onClose={() => setArtifactDialogOpen(false)}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>
            Artifact Preview:{" "}
            {artifactPreview?.tableName || selectedArtifact?.tableName || "-"}
          </DialogTitle>
          <DialogContent>
            <Stack
              spacing={1}
              mb={1}
            >
              <Typography variant="body2">
                Batch:{" "}
                {artifactPreview?.extractBatchId ||
                  selectedArtifact?.extractBatchId ||
                  "-"}
              </Typography>
              <Typography variant="body2">
                Records:{" "}
                {artifactPreview?.recordCount ??
                  selectedArtifact?.recordCount ??
                  0}
              </Typography>
              <Typography variant="body2">
                Showing records {artifactPreviewOffset + 1} to{" "}
                {Math.min(
                  artifactPreviewOffset + artifactPreviewLimit,
                  Number(
                    artifactPreview?.recordCount ||
                      selectedArtifact?.recordCount ||
                      0,
                  ),
                )}
              </Typography>
            </Stack>

            <TextField
              fullWidth
              multiline
              minRows={14}
              value={
                artifactPreview
                  ? JSON.stringify(artifactPreview.recordsPage || [], null, 2)
                  : "[]"
              }
              InputProps={{ readOnly: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                loadArtifactPreview(
                  getId(selectedArtifact),
                  Math.max(0, artifactPreviewOffset - artifactPreviewLimit),
                )
              }
              disabled={loadingArtifactPreview || artifactPreviewOffset <= 0}
            >
              Previous
            </Button>
            <Button
              onClick={() =>
                loadArtifactPreview(
                  getId(selectedArtifact),
                  artifactPreviewOffset + artifactPreviewLimit,
                )
              }
              disabled={
                loadingArtifactPreview ||
                artifactPreviewOffset + artifactPreviewLimit >=
                  Number(
                    artifactPreview?.recordCount ||
                      selectedArtifact?.recordCount ||
                      0,
                  )
              }
            >
              Next
            </Button>
            <Button onClick={() => setArtifactDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={compareDialogOpen}
          onClose={() => setCompareDialogOpen(false)}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>Extract vs Server Compare</DialogTitle>
          <DialogContent>
            {compareLoading ? (
              <Typography variant="body2">Comparing...</Typography>
            ) : null}

            {!compareLoading && compareResult ? (
              <Stack
                spacing={1}
                mt={0.5}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                >
                  <Chip
                    size="small"
                    label={`Table: ${compareResult.tableName || "-"}`}
                  />
                  <Chip
                    size="small"
                    label={`Extracted: ${compareResult.extractedCount || 0}`}
                  />
                  <Chip
                    size="small"
                    label={`Matched: ${compareResult.matchedInServerCount || 0}`}
                    color="success"
                  />
                  <Chip
                    size="small"
                    label={`Missing: ${compareResult.missingInServerCount || 0}`}
                    color={
                      Number(compareResult.missingInServerCount || 0) > 0
                        ? "error"
                        : "default"
                    }
                  />
                  <Chip
                    size="small"
                    label={`Changed: ${compareResult.changedCount || 0}`}
                    color={
                      Number(compareResult.changedCount || 0) > 0
                        ? "warning"
                        : "default"
                    }
                  />
                  <Chip
                    size="small"
                    label={`Unchanged: ${compareResult.unchangedCount || 0}`}
                  />
                  {compareResult.imageSpaceCheck?.enabled ? (
                    <>
                      <Chip
                        size="small"
                        label={`Image Refs Checked: ${compareResult.imageSpaceCheck.imageRefsChecked || 0}`}
                      />
                      <Chip
                        size="small"
                        label={`Missing Images In Spaces: ${compareResult.imageSpaceCheck.missingImageCount || 0}`}
                        color={
                          Number(
                            compareResult.imageSpaceCheck.missingImageCount ||
                              0,
                          ) > 0
                            ? "error"
                            : "default"
                        }
                      />
                    </>
                  ) : null}
                </Stack>

                {compareResult.tableName === "surveyanswers" &&
                !compareResult.imageSpaceCheck?.enabled &&
                compareResult.imageSpaceCheck?.skippedReason ? (
                  <Alert severity="warning">
                    Image presence check skipped:{" "}
                    {compareResult.imageSpaceCheck.skippedReason}
                  </Alert>
                ) : null}

                {Number(compareResult.missingInServerCount || 0) === 0 &&
                Number(compareResult.changedCount || 0) === 0 ? (
                  <Alert severity="success">
                    No differences found between extract and current server
                    records for compared fields.
                  </Alert>
                ) : null}

                {Number(compareResult.missingInServerCount || 0) > 0 ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={8}
                    label="Missing In Server (sample)"
                    value={JSON.stringify(
                      compareResult.missingInServer || [],
                      null,
                      2,
                    )}
                    InputProps={{ readOnly: true }}
                  />
                ) : null}

                {Number(compareResult.changedCount || 0) > 0 ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={10}
                    label="Changed Records (sample)"
                    value={JSON.stringify(
                      compareResult.changedRecords || [],
                      null,
                      2,
                    )}
                    InputProps={{ readOnly: true }}
                  />
                ) : null}

                {Number(compareResult.imageSpaceCheck?.missingImageCount || 0) >
                0 ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={8}
                    label="Missing Images In Spaces (sample)"
                    value={JSON.stringify(
                      compareResult.imageSpaceCheck?.missingImagesInSpaces ||
                        [],
                      null,
                      2,
                    )}
                    InputProps={{ readOnly: true }}
                  />
                ) : null}
              </Stack>
            ) : null}
          </DialogContent>
          <DialogActions>
            {compareResult &&
            (Number(compareResult.missingInServerCount || 0) > 0 ||
              Number(compareResult.changedCount || 0) > 0) ? (
              <Button
                variant="contained"
                color="warning"
                onClick={handleResolveConflicts}
                disabled={resolvingConflicts || compareLoading}
              >
                {resolvingConflicts ? "Resolving..." : "Resolve Conflicts"}
              </Button>
            ) : null}
            {compareResult?.tableName === "surveyanswers" &&
            compareResult.imageSpaceCheck?.enabled &&
            Number(compareResult.imageSpaceCheck?.missingImageCount || 0) >
              0 ? (
              <Button
                variant="contained"
                onClick={handleRecoverMissingImages}
                disabled={recoveringImages || compareLoading}
              >
                {recoveringImages ? "Recovering..." : "Recover Missing Images"}
              </Button>
            ) : null}
            <Button onClick={() => setCompareDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Card>
  );
};

export default ForensicRequestManager;
